/**
 * Which row-filter keys the BACKEND can still resolve, and which are dead.
 *
 * The authorizer repairs a stored row-filter key on every resolve
 * (`GuestFilterKeyNormalizer.normalizeKey`): a key that already carries a dot is
 * left alone, a bare key that is a filterable built-in is left alone, and
 * anything else is assumed to be a custom field and prefixed with
 * `additionalFields.`. If the event's GUEST catalogue then has no such field, the
 * whole scope collapses to an unsatisfiable sentinel — the role sees ZERO guests
 * on every lane.
 *
 * That makes "is this key resolvable" a question the editor has to be able to
 * answer, and it cannot answer it from the field catalogue alone: five filterable
 * built-ins have no `FIELDS_GUEST` def at all (`acceptanceState`, `submittedAt`,
 * `mainGuest`, `eventId`, `wasWaitlisted`), so "not in the catalogue" would
 * condemn keys the backend resolves perfectly well.
 */
import type { Field } from "@/types/Field";
import {
  ADDITIONAL_FIELDS_PREFIX,
  findFieldByKey,
} from "@/components/ConditionBuilder/condition-utils";
import { OPTION_VALUE_TYPES, isEmptyOperand } from "@/lib/utils/filterFieldSwap";

/**
 * `GuestService.ALLOWED_GUEST_FIELDS` minus the three projection-only tokens the
 * backend also subtracts (`GuestFilterKeyNormalizer.PROJECTION_ONLY_KEYS`) —
 * i.e. exactly the bare keys `normalizeKey` leaves alone.
 *
 * ⚠ KEEP IN SYNC with main-backend. This is a mirror, and it is a mirror on
 * purpose: reading the Java file at test time is the stronger check but cannot be
 * the only one, because CI checks out a single repo (`pages.yml` is a plain
 * `actions/checkout@v4`) and runs the suite inside the Pages deploy job.
 * `RowFilterRow.wireKeys.test.tsx` asserts this set against
 * `GuestService.java` whenever the sibling repo is on disk, which is every local
 * and agent run — that test is what catches drift in either direction.
 */
export const FILTERABLE_BUILTIN_KEYS = new Set([
  "acceptanceState", "guestId", "eventId", "guestType", "mainGuest",
  "state", "gender", "title", "firstName", "lastName", "company",
  "jobTitle", "address", "address2", "zipCode", "city", "country",
  "email", "phone", "mobile", "language", "username", "code", "archived",
  "createdAt", "updatedAt", "submittedAt", "emailOptIn",
  "additionalFields", "checkedIn", "checkedInAt", "numCompanions",
  "companions", "apptStatuses", "apptCount", "nextApptAt",
  "hasOpenRequest", "apptOfferingIds", "apptTreatmentIds", "apptLaneIds",
  "seatStatuses", "seatCount", "seatRoomIds", "lastEmailedAt",
  "lastEmailStatus", "lastEmailOpenedAt", "lastEmailClickedAt",
  "emailedTemplateIds", "emailedSendNames", "wasWaitlisted",
  "portalVisitedAt", "websiteLoginAt", "origin", "importId", "speakerId",
  "sponsorId",
  // Added backend-side by PB-TH-7 (un-suppressing a bounced address) and never
  // mirrored here. Drift in this direction is not harmless: the key verdict
  // would call a filter the authorizer resolves perfectly well "deleted" and
  // invite the admin to remove a scope that is being enforced.
  "emailUndeliverable", "emailUndeliverableAt",
  // SMS channel wave: the SMS consent is a bare filter key (main-backend B2,
  // strict boolean in FilterParser — only `true` is yes). smsOptInAt /
  // smsOptInSource are projection-only there (PROJECTION_ONLY_KEYS), so they
  // stay out. The picker only offers smsOptIn while the channel is on
  // (FieldsProvider drops the field otherwise).
  "smsOptIn",
]);

/**
 * How the backend will judge this stored key. Mirrors `normalizeKey` +
 * `normalizeAll`'s catalogue test, and nothing else — the point is that the row
 * the admin reads and the row the authorizer enforces agree.
 *
 *  · `resolvable`   — enforced as written (or as `additionalFields.<name>`).
 *  · `dead`         — normalizes into `additionalFields.<something unknown>`, so
 *                     the sentinel fires and the role sees no guests at all. This
 *                     covers a deleted custom field in EITHER shape AND the
 *                     presentation-only columns the old picker offered
 *                     (`seating`, `attachments`, `slotting`, `id`,
 *                     `checkInOutCTA`), which were never filterable keys.
 *  · `unjudged`     — a dotted key that is not `additionalFields.*`
 *                     (`seatInfo.roomId`, …). `normalizeKey` returns it verbatim,
 *                     so it is neither repaired nor sentinel'd: the validating
 *                     lanes reject it with `unknownFilterKey` while the others
 *                     read null, where `≠`/`∅` match everything. Only reachable
 *                     on data written straight through the API before the save
 *                     gate existed — the picker never produced a dot.
 */
export type RowFilterKeyVerdict = "resolvable" | "dead" | "unjudged";

export function rowFilterKeyVerdict(
  key: string | undefined,
  fields: Field[],
): RowFilterKeyVerdict {
  if (!key) return "resolvable";
  if (key.startsWith(ADDITIONAL_FIELDS_PREFIX)) {
    return findFieldByKey(fields, key) ? "resolvable" : "dead";
  }
  if (key.includes(".")) return "unjudged";
  if (FILTERABLE_BUILTIN_KEYS.has(key)) return "resolvable";
  // A bare key the backend does not know: it gets the `additionalFields.` prefix,
  // so it is resolvable exactly as long as a custom field of that name exists.
  return findFieldByKey(fields, `${ADDITIONAL_FIELDS_PREFIX}${key}`)
    ? "resolvable"
    : "dead";
}

/**
 * The same question one column to the right: the key resolves, but does the
 * OPERAND still name an option that exists?
 *
 * Why this needs saying at all. A rename now cascades — every store that saved
 * the label is rewritten, so a renamed option never lands here. A **removed**
 * option is the case no cascade can fix, and deliberately so: removing it takes
 * the value off the guests, so `= VIP` rightly matches nobody and `≠ VIP`
 * rightly matches everybody. Deleting the row instead would be the one action
 * that genuinely widens the role's scope (an empty row-filter list means
 * "unrestricted"). Both options are wrong to take automatically — so the admin
 * decides, informed. The backend's value gate additionally grandfathers such a
 * row so the template can still be saved; that turned a loud 400 into silence,
 * and this verdict is what puts the information back on screen.
 *
 *  · `live`             — the operand is a real option, or nothing here can be
 *                         judged (see the escapes below).
 *  · `selectsNobody`    — `=` against an option that no longer exists. The role
 *                         sees zero guests through this row.
 *  · `selectsEveryone`  — `≠` against one. The restriction is silently gone.
 *
 * Judged ONLY for the two exact-equality operators, which is the same line the
 * backend cascade draws: `~` is a substring (an operand that is deliberately not
 * a whole option), and `> ≥ < ≤` compare against a boundary rather than test
 * membership. Claiming "dead" for those would condemn working rows.
 */
export type RowFilterValueVerdict = "live" | "selectsNobody" | "selectsEveryone";

export function rowFilterValueVerdict(
  key: string | undefined,
  operator: string | undefined,
  value: string | string[] | undefined,
  fields: Field[],
): RowFilterValueVerdict {
  if (operator !== "=" && operator !== "≠") return "live";
  // "" is the "—" (is-empty) pick every value cell offers — a legal operand
  // against any option-typed field, not a dead one.
  if (isEmptyOperand(value)) return "live";
  const field = key ? findFieldByKey(fields, key) : undefined;
  // An unresolved field proves nothing about its operand, and the KEY verdict
  // already says its piece about that row. Two markers for one broken row would
  // read as two problems.
  if (!field || !OPTION_VALUE_TYPES.has(field.dataType)) return "live";
  // No list, no membership question. BOOLEAN owns no `options` at all (its
  // vocabulary lives in the cell), and a custom select may legitimately have an
  // empty one.
  //
  // This also covers the in-flight `optionSource` fetch, which is the case that
  // matters most: while it runs, every stored id would look off-list and the
  // editor would flash a false "selects nobody" on every open. There is
  // deliberately no separate `optionsPending` arm — that flag is defined as
  // exactly the window in which `options` is empty (see Field.optionsPending),
  // so a second check could never fire on its own and would be a guard no test
  // could ever turn red.
  const options = field.options;
  if (!options?.length) return "live";
  const parts = Array.isArray(value) ? value : [value ?? ""];
  // Only when EVERY non-empty part is gone. A multi-pick that still holds one
  // real option is not "selects nobody" — it selects less than it used to, which
  // is a different (and unprovable) claim.
  const allGone = parts.every((p) => p === "" || !options.includes(p));
  if (!allGone) return "live";
  return operator === "=" ? "selectsNobody" : "selectsEveryone";
}
